import moment from "moment"
import { Rate } from "antd"
import { TableOutlined, UnorderedListOutlined } from '@ant-design/icons'

import { IApplicationsPage } from "./types"
import { ApplicationsTable } from './ui.table'
import { IApplicationsList } from "@api/applications/state.types"

import { useDeviceType } from "@hooks/useDeviceType"
import CommonService from "@services/common.service"
import { ComponentsHeader } from "@primitive/sectionHeadings"

import {
    Badge, Button, Container, Empty, Flex,
    Loader, Pagination, ToolTip, Table
} from "@ui/index"


import {
    IconApplications, IconContracts, IconTelegram,
    IconArchiveAdd, IconArchiveDelete,
    IconMAX,
    IconTG,
    IconCall,
    IconWhatsApp
} from "@img/icons"

import imgEmty from '@img/empty.svg'
import imgAccessDenied from '@img/access_denied.svg'
import logoModSkill from '@img/logo_modskill.svg'
import logoYaSkill from '@img/logo_yaskill.svg'

import css from './ui.module.scss'


/** Страница - Заявки */
export const ApplicationsPage = ({
    isLoadingPage, isAccessDenied, applicationsList, applicationsStatistics,
    applicationsTableColumns, loginName, viewMode, columnFilters, onViewModeChange, onColumnFilterChange, onClearColumnFilters, loadColumnFilterOptions,
    applicationsPageSize, paginationPageNumber,
    applicationsTotalCount, applicationsNew, applicationsDelayedCall, applicationsExecutionContract, viewedDelayedActionKeys,
    filterIsOpen, filterIsActivate, txtSearch, setTxtSearch,
    loadPrimaryData, handleOpenModalConfirm, handleOpenModalMailings,
    handleOpenCardApplication, handleOpenCardApplicationPromo, handleApplyClearFilter
}: IApplicationsPage) => {
    const deviceType = useDeviceType()

    type ContactActionIconType = 'max' | 'telegram' | 'call' | 'whatsapp'
    type ContactActionType = {
        href: string
        iconType: ContactActionIconType
        isExternal: boolean
    }

    /** Вазвращает лого сайта */
    const returnLogo = (dbName: string) => {
        switch (dbName) {
            case 'modern_academy': return logoModSkill
            case 'yaskill': return logoYaSkill
        }
    }

    const getNormalizedPhone = (phone: string) => phone.replace(/[^\d+]/g, '')
    const getDelayedActionViewKey = (item: IApplicationsList) => `${item.applicationID}|${item.dateNextCall || ''}|${item.timeNextCall || ''}`

    const getContactActionIcon = (iconType: ContactActionIconType) => {
        const iconSize = deviceType === 'mobile' ? '16px' : '20px'

        switch (iconType) {
            case 'max':
                return <IconMAX width={iconSize} height={iconSize} color="#DC1C2E" />
            case 'telegram':
                return <IconTG width={iconSize} height={iconSize} color="#0088CC" />
            case 'call':
                return <IconCall width={iconSize} height={iconSize} color="green" />
            case 'whatsapp':
                return <IconWhatsApp width={iconSize} height={iconSize} color="#25D366" />
        }
    }

    const getApplicationContactAction = (item: IApplicationsList): ContactActionType | null => {
        const preferredContactMethod = `${item.preferredContactMethod || ''}`.trim().toLowerCase()
        const maxChatUrl = CommonService.normalizeHttpsUrl(item.maxChatUrl)
        const telegramChatUrl = CommonService.normalizeHttpsUrl(item.telegramChatUrl)
        const callHref = item.clientPhone ? `tel:${getNormalizedPhone(item.clientPhone)}` : ''

        if (!preferredContactMethod) return null

        switch (preferredContactMethod) {
            case 'phone': {
                if (maxChatUrl) return { href: maxChatUrl, iconType: 'max', isExternal: true }
                if (telegramChatUrl) return { href: telegramChatUrl, iconType: 'telegram', isExternal: true }
                if (callHref) return { href: callHref, iconType: 'call', isExternal: false }
                return null
            }

            case 'whatsapp': {
                if (maxChatUrl) return { href: maxChatUrl, iconType: 'max', isExternal: true }
                if (telegramChatUrl) return { href: telegramChatUrl, iconType: 'telegram', isExternal: true }
                if (callHref) return { href: callHref, iconType: 'whatsapp', isExternal: false }
                return null
            }

            case 'max': {
                if (maxChatUrl) return { href: maxChatUrl, iconType: 'max', isExternal: true }
                return null
            }

            case 'telegram': {
                if (maxChatUrl) return { href: maxChatUrl, iconType: 'max', isExternal: true }
                if (telegramChatUrl) return { href: telegramChatUrl, iconType: 'telegram', isExternal: true }
                return null
            }

            default:
                return null
        }
    }

    const openApplicationContactAction = (action: ContactActionType) => {
        if (action.isExternal) {
            window.open(action.href, '_blank', 'noopener,noreferrer')
            return
        }

        window.location.href = action.href
    }

    const renderApplicationContactAction = (item: IApplicationsList) => {
        const contactAction = getApplicationContactAction(item)
        if (!contactAction) return null

        return <ToolTip title={contactAction.isExternal ? 'Открыть чат' : 'Позвонить'} placement="bottomRight">
            <Button variant="icon" size="xs" onClick={(event) => {
                event.stopPropagation()
                openApplicationContactAction(contactAction)
            }}>
                {getContactActionIcon(contactAction.iconType)}
            </Button>
        </ToolTip>
    }

    return <div className={css['container']}>
        
        <ComponentsHeader
            isAccessDenied={isAccessDenied.value}
            headerIcon={<IconApplications width="20px" height="20px" />}
            headerTitle="Заявки"
            actions={<div className={css.viewSwitch} role="group" aria-label="Вид заявок">
                <ToolTip title="Прежний вид" placement="bottomLeft">
                    <button type="button" aria-label="Прежний вид" aria-pressed={viewMode === 'list'} onClick={() => onViewModeChange('list')}>
                        <UnorderedListOutlined /><span>Список</span>
                    </button>
                </ToolTip>
                <ToolTip title="Табличный вид" placement="bottomLeft">
                    <button type="button" aria-label="Табличный вид" aria-pressed={viewMode === 'table'} onClick={() => onViewModeChange('table')}>
                        <TableOutlined /><span>Таблица</span>
                    </button>
                </ToolTip>
            </div>}
            txtSearch={txtSearch}
            txtSearchTooltip={<>Поиск по:<br />✓ Номеру заявки<br />✓ Имени клиента<br />✓ Телефону клиента<br />✓ Email клиента<br />✓ Имени курса<br />✓ Комментарию</>}
            txtSearchOnChange={(e) => setTxtSearch(e.target.value)}
            txtSearchClearOnClick={() => setTxtSearch('')}
            btnUpdateOnClick={() => loadPrimaryData(txtSearch)}
            btnCreateOnClick={() => handleOpenCardApplication(true, "new")}
            btnFilterOnClick={() => filterIsOpen.setValue(true)}
            btnFilterClearOnClick={() => handleApplyClearFilter('clear')}
            filterIsActivate={filterIsActivate.value || (viewMode === 'table' && Object.values(columnFilters).some(values => values.length > 0))}
            btnPromoOnClick={() => handleOpenCardApplicationPromo(true)}
        />                    

        {viewMode === 'table' ? <ApplicationsTable
            isLoading={isLoadingPage}
            applicationsList={applicationsList} columns={applicationsTableColumns} loginName={loginName} filters={columnFilters}
            isAccessDenied={isAccessDenied.value} viewedDelayedActionKeys={viewedDelayedActionKeys}
            onFilterChange={onColumnFilterChange} onClearFilters={onClearColumnFilters}
            loadFilterOptions={loadColumnFilterOptions}
            renderContactAction={renderApplicationContactAction}
            handleOpenCardApplication={handleOpenCardApplication} handleOpenModalConfirm={handleOpenModalConfirm}
            handleOpenModalMailings={handleOpenModalMailings}
        /> : <Table.Container>
            <Loader isOpen={isLoadingPage} backdrop color="var(--foreground_accent_default)" title="Загрузка..." />
            <Table.Header paddingLeft="53px" paddingRight="23px" classNameContainer={css['hidden_800']} classNameContent={css['table_grid_columns_header']}>
                <p style={{ textAlign: 'center' }}>№<br />заявки</p>

                <div>
                    <p>Имя клиента</p>
                    <p>Телефон клиента</p>
                </div>

                <div>
                    <p>Комментарий</p>
                    <p>Часы / Программа</p>
                </div>

                <p className={css['hidden_950']} style={{ textAlign: 'center' }}>Отложенное<br />действие</p>
                <p className={css['hidden_1150']} style={{ textAlign: 'center' }}>Статус<br />заявки</p>
                <p className={css['hidden_1050']} style={{ textAlign: 'center' }}>Дата<br />создания</p>

                <p className={`${css['hidden_800']}`} style={{ textAlign: 'center' }}>Действие</p>
            </Table.Header>

            <Table.Content>
                {applicationsList.length === 0
                    ? <Empty image={isAccessDenied.value === false ? imgEmty : imgAccessDenied} title={isAccessDenied.value === false ? 'Данные отсутствуют' : 'Доступ запрещён'} />
                    : applicationsList.map((item) => {
                        const delayedActionViewKey = getDelayedActionViewKey(item)
                        const isDelayedActionPulseVisible = item.isDelayedActionExpired === 1 && !viewedDelayedActionKeys.includes(delayedActionViewKey)

                        return (
                            <Table.Row key={item.applicationID} className={`${css['table_grid_columns_content']} ${isDelayedActionPulseVisible ? css['table_row_delayed_action_expired'] : ''}`} onClick={() => handleOpenCardApplication(true, "edit", item.applicationID, isDelayedActionPulseVisible ? delayedActionViewKey : undefined)}>
                                <img src={returnLogo(item.dbName)} alt="Лого сайта" />

                                <div style={{
                                    color: '#ffffff',
                                    textAlign: 'center',
                                    padding: '4px 0px',
                                    lineHeight: '90%',
                                    background: item.applicationStatusColor,
                                    borderRadius: 'calc(var(--border_radius_default) - 4px)',
                                }}>
                                    <p>{item.applicationID}</p>
                                    <Rate disabled size="small" value={item.clientRating} className="custom-rate" />
                                </div>

                                <div style={{ textWrap: 'nowrap', overflow: 'hidden' }}>
                                    <Flex gap="6px" flexDirection="row" alignItems="center">
                                        {renderApplicationContactAction(item)}

                                        <p>{item.clientName}</p>
                                    </Flex>

                                    <Flex gap="6px" flexDirection="row">
                                        <p style={{ opacity: '.7' }}>{item.clientPhone}</p>
                                        <p>{item.isRepeat === 1 ? '🔥' : ''}</p>
                                    </Flex>
                                </div>

                                <div className={css['hidden_800']} style={{ textWrap: 'nowrap', overflow: 'hidden' }}>
                                    <p>{item.applicationComment || '------'}</p>
                                    <p style={{ opacity: '.7' }}>{item.volumeHours ? CommonService.formatHours(item.volumeHours) : '------'} / {item.courseName || '------'}</p>
                                </div>

                                <div className={css['hidden_950']} style={{ textAlign: 'center' }}>
                                    <p>{item.dateNextCall || '--.--.----'}</p>
                                    <p>{item.timeNextCall || '--:--:--'}</p>
                                </div>

                                <div className={`${css['hidden_1150']}`} style={{
                                    textAlign: 'center',
                                    fontWeight: '600',
                                    color: item.applicationStatusColor,
                                }}>{item.applicationStatusName}</div>

                                <div className={css['hidden_1050']} style={{ textAlign: 'center' }}>
                                    {moment(item.dateCreate).format('DD.MM.YYYY')}<br />{moment(item.dateCreate).format('HH:mm:ss')}
                                </div>

                                <Flex gap="2px" flexDirection="row" alignItems="center" justifyContent="center">
                                    <ToolTip title='Создать договор' placement="bottomRight" isVisible={(deviceType === 'desktop' && item.applicationStatusID === 3)}>
                                        <Button variant="icon" size={deviceType === 'mobile' ? 'xs' : 'sm'}
                                            isDisabled={item.applicationStatusID !== 3}
                                            onClick={(e) => { handleOpenModalConfirm(true, 'Создание договора', `Вы действительно хотите создать договор по заявке № ${item.applicationID}`, item.applicationID, item.dbName, "createContract", 0); e.stopPropagation() }}
                                        >
                                            {(item.contractCount > 0 || item.applicationStatusID === 3) &&
                                                <Badge left="-6px" top="-6px" background={item.contractCount === 0
                                                    ? 'red'
                                                    : '#0000FF'
                                                }>{item.contractCount}</Badge>
                                            }

                                            <IconContracts width={deviceType === 'mobile' ? '16px' : '20px'} height={deviceType === 'mobile' ? '16px' : '20px'} />
                                        </Button>
                                    </ToolTip>

                                    <ToolTip title='Рассылки' placement="bottomRight">
                                        <Button variant="icon" size={deviceType === 'mobile' ? 'xs' : 'sm'}
                                            onClick={(e) => {
                                                handleOpenModalMailings(true, item.applicationID)
                                                e.stopPropagation()
                                            }}
                                        ><IconTelegram width={deviceType === 'mobile' ? '16px' : '20px'} height={deviceType === 'mobile' ? '16px' : '20px'} /></Button>
                                    </ToolTip>

                                    <ToolTip title='В чёрный лист' placement="bottomRight">
                                        <Button variant="icon" size={deviceType === 'mobile' ? 'xs' : 'sm'}
                                            onClick={(e) => { handleOpenModalConfirm(true, 'Отправка в чёрный лист', `${item.isBlackList === 0 ? `Вы действительно хотите отправить заявку № ${item.applicationID} в чёрный лист?` : `Вы действительно хотите удалить заявку № ${item.applicationID} из чёрного листа?`}`, item.applicationID, item.dbName, "toBlackList", item.isBlackList); e.stopPropagation() }}
                                        >
                                            {item.isBlackList === 0
                                                ? <IconArchiveAdd width={deviceType === 'mobile' ? '16px' : '20px'} height={deviceType === 'mobile' ? '16px' : '20px'} />
                                                : <IconArchiveDelete width={deviceType === 'mobile' ? '16px' : '20px'} height={deviceType === 'mobile' ? '16px' : '20px'} color="green" />
                                            }
                                        </Button>
                                    </ToolTip>
                                </Flex>
                            </Table.Row>
                        )
                    })
                }
            </Table.Content>
        </Table.Container>}

        <Container padding="6px" className={css['footer']} overflow="visible">
            <Flex gap="4px" flexDirection="row">
                <ToolTip title='Общее количество' placement="topLeft">
                    <p className={css['stat_all']}>{applicationsTotalCount}</p>
                </ToolTip>

                {applicationsNew > 0 && 
                    <ToolTip title='Новая' placement="topLeft">
                        <p className={css['stat_new']}>{applicationsNew}</p>
                    </ToolTip>
                }

                {applicationsDelayedCall > 0 && 
                    <ToolTip title='Отложенное действие' placement="topLeft">
                        <p className={css['stat_delayed']}>{applicationsDelayedCall}</p>
                    </ToolTip>
                }

                {applicationsExecutionContract > 0 && 
                    <ToolTip title='Оформление договора' placement="topLeft">
                        <p className={css['stat_execution_contract']}>{applicationsExecutionContract}</p>
                    </ToolTip>
                }
            </Flex>

            <Pagination
                totalCount={applicationsTotalCount}
                pageSize={applicationsPageSize}
                currentPage={paginationPageNumber.value}
                onChange={(e) => paginationPageNumber.setValue(e)}
            />
        </Container>
    </div>
}